import React, { useState } from 'react'
import useBreakpoint from '#src/hooks/useBreakpoint'
import SliderWithScrollLock, { CustomArrowProps } from '#src/components/SliderWithScrollLock'
import { Image } from '@deal/components'
import { MediaFileFragment } from '#src/generated/graphql'
import ArrowLeft from './arrow-left.svg'
import ArrowRight from './arrow-right.svg'
import Close from './close.svg'
import ViewPhotos from './view-photos.svg'
import classnames from 'classnames'
import styles from './styles.css'
import PageContent from '#src/components/PageContent'

enum ImageContext {
  MOBILE,
  DESKTOP
}

interface ImageCarouselProps {
  images: MediaFileFragment[]
}

interface CarouselImageProps {
  imageClassName?: string
  image?: MediaFileFragment
  context: ImageContext
}

const Arrow: React.SFC<CustomArrowProps> = ({ children, className, onClick }) => {
  return (
    <div className={classnames(className, styles.arrow)} onClick={onClick}>
      {children}
    </div>
  )
}

const NextArrow: React.SFC<CustomArrowProps> = ({ className, ...rest }) => {
  return (
    <Arrow className={classnames(className, styles.nextArrow)} {...rest}>
      <ArrowRight className={styles.arrowIcon} />
    </Arrow>
  )
}

const PrevArrow: React.SFC<CustomArrowProps> = ({ className, ...rest }) => {
  return (
    <Arrow className={classnames(className, styles.prevArrow)} {...rest}>
      <ArrowLeft className={styles.arrowIcon} />
    </Arrow>
  )
}

const CarouselImage: React.SFC<CarouselImageProps> = ({ image, imageClassName, context }) => {
  if (!image) {
    return null
  }

  return context === ImageContext.DESKTOP ? (
    <div className={styles.desktopSliderImage}>
      <div className={styles.mask} />
      <Image
        className={imageClassName}
        lazyLoad={false}
        src={image.url}
        width={1140}
        height={500}
        imgixParams={{ fit: 'crop' }}
      />
    </div>
  ) : (
    <div className={styles.mobileSliderImage}>
      <Image
        lazyLoad={false}
        src={image.url}
        className={styles.mobileImage}
        imgixParams={{ fit: 'fill' }}
        sizes="100vw"
      />
    </div>
  )
}

const ImageCarousel: React.SFC<ImageCarouselProps> = ({ images }) => {
  if (images.length === 0) {
    return null
  }

  let mutableImages = images.slice()
  const [isImageSliderExpanded, toggleImageSlider] = useState(false)

  /**
   * The lazy loading starts on slide 2 so we load 'left to right' on desktop. Since the
   * first slide is often hand picked (as well as the order), we want to move everything
   * over by one.
   */
  const shiftedImages = [mutableImages.pop(), ...mutableImages]
  /**
   * We start with only showing the first three slides as 'visible'. The remaining slides are
   * marked as visibility: hidden; which allows boomerang to correctly dispatch the beacon
   * at the right time.
   */
  const [visibleImageIndexes, updateImageIndexes] = useState([0, 1, 2])

  const isDesktop = useBreakpoint('md')

  return isDesktop ? (
    <PageContent sidebar={true} gridClassName={styles.desktopSlider}>
      <SliderWithScrollLock
        centerMode={true}
        centerPadding="0px"
        slidesToShow={1}
        initialSlide={1}
        variableWidth={true}
        nextArrow={<NextArrow />}
        prevArrow={<PrevArrow />}
        className={styles.slider}
        beforeChange={() => {
          if (visibleImageIndexes.length !== shiftedImages.length) {
            /**
             * Once we've navigated once, mark all of these as visible
             */
            updateImageIndexes(shiftedImages.map((_result, idx) => idx))
          }
        }}
        responsive={[
          {
            breakpoint: 768,
            settings: {
              arrows: false,
              centerMode: true,
              centerPadding: '0px',
              slidesToShow: 1
            }
          },
          {
            breakpoint: 480,
            settings: {
              arrows: true,
              centerMode: true,
              centerPadding: '0px',
              slidesToShow: 1
            }
          }
        ]}
      >
        {shiftedImages.map((image, idx) => {
          const carouselImageClass = !visibleImageIndexes.includes(idx)
            ? styles.hiddenSlide
            : undefined

          return (
            <CarouselImage
              imageClassName={carouselImageClass}
              image={image}
              key={idx}
              context={ImageContext.DESKTOP}
            />
          )
        })}
      </SliderWithScrollLock>
    </PageContent>
  ) : (
    <>
      <div className={styles.mobileSlider} onClick={() => toggleImageSlider(true)}>
        <CarouselImage image={images[0]} context={ImageContext.MOBILE} />
        <button className={styles.absoluteIcon}>
          <ViewPhotos />
        </button>
      </div>
      {isImageSliderExpanded && (
        <div className={styles.popSlider}>
          <div className={styles.popImages}>
            {images.map((image, idx) => (
              <CarouselImage image={image} key={idx} context={ImageContext.MOBILE} />
            ))}
          </div>
          <button className={styles.fixedIcon} onClick={() => toggleImageSlider(false)}>
            <Close />
          </button>
        </div>
      )}
    </>
  )
}

export default ImageCarousel
